import React from 'react';
import PropTypes from 'prop-types';
import { getTokensPropType, selectSystemProps, variantProp, useTheme, StyleSheet, createMediaQueryStyles, useAllViewportTokens } from '@telus-uds/components-base';
import styled from 'styled-components';
import { htmlAttrs } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs]);
const CardContentContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "CardContent__CardContentContainer",
  componentId: "components-web__sc-1k2501q-0"
})(_ref => {
  let {
    borderRadius,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    withFooter,
    contentAlignItems: alignItems,
    contentFlexGrow: flexGrow,
    contentFlexShrink: flexShrink,
    contentJustifyContent: justifyContent,
    borderWidth,
    alignSelf
  } = _ref;
  return {
    // We need to make sure to have sharp corners on the bottom
    // if the card has a footer
    borderBottomLeftRadius: withFooter ? 0 : borderRadius,
    borderBottomRightRadius: withFooter ? 0 : borderRadius,
    borderTopLeftRadius: borderRadius,
    borderTopRightRadius: borderRadius,
    paddingBottom: paddingBottom - borderWidth,
    paddingLeft: paddingLeft - borderWidth,
    paddingRight: paddingRight - borderWidth,
    paddingTop: paddingTop - borderWidth,
    display: 'flex',
    flexDirection: 'column',
    alignItems,
    flexGrow,
    flexShrink,
    justifyContent,
    alignSelf
  };
});

/**
 * Card content, applying the card tokens as per the theme used.
 */
const CardContent = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    children,
    flexContent,
    tokens,
    variant,
    withFooter = false,
    ...rest
  } = _ref2;
  const {
    themeOptions: {
      enableMediaQueryStyleSheet
    }
  } = useTheme();
  const allTokens = useAllViewportTokens('Card', tokens, variant);
  let themeTokens;
  let mediaIds;
  if (enableMediaQueryStyleSheet) {
    const stylesByViewport = {
      xs: {
        paddingBottom: allTokens.xs.paddingBottom - allTokens.xs.borderWidth,
        paddingLeft: allTokens.xs.paddingLeft - allTokens.xs.borderWidth,
        paddingRight: allTokens.xs.paddingRight - allTokens.xs.borderWidth,
        paddingTop: allTokens.xs.paddingTop - allTokens.xs.borderWidth
      },
      sm: {
        paddingBottom: allTokens.sm.paddingBottom - allTokens.sm.borderWidth,
        paddingLeft: allTokens.sm.paddingLeft - allTokens.sm.borderWidth,
        paddingRight: allTokens.sm.paddingRight - allTokens.sm.borderWidth,
        paddingTop: allTokens.sm.paddingTop - allTokens.sm.borderWidth
      },
      md: {
        paddingBottom: allTokens.md.paddingBottom - allTokens.md.borderWidth,
        paddingLeft: allTokens.md.paddingLeft - allTokens.md.borderWidth,
        paddingRight: allTokens.md.paddingRight - allTokens.md.borderWidth,
        paddingTop: allTokens.md.paddingTop - allTokens.md.borderWidth
      },
      lg: {
        paddingBottom: allTokens.lg.paddingBottom - allTokens.lg.borderWidth,
        paddingLeft: allTokens.lg.paddingLeft - allTokens.lg.borderWidth,
        paddingRight: allTokens.lg.paddingRight - allTokens.lg.borderWidth,
        paddingTop: allTokens.lg.paddingTop - allTokens.lg.borderWidth
      },
      xl: {
        paddingBottom: allTokens.xl.paddingBottom - allTokens.xl.borderWidth,
        paddingLeft: allTokens.xl.paddingLeft - allTokens.xl.borderWidth,
        paddingRight: allTokens.xl.paddingRight - allTokens.xl.borderWidth,
        paddingTop: allTokens.xl.paddingTop - allTokens.xl.borderWidth
      }
    };
    const mediaQueryStyles = createMediaQueryStyles(stylesByViewport);
    const {
      ids,
      styles
    } = StyleSheet.create({
      cardContent: {
        ...allTokens.current,
        ...mediaQueryStyles
      }
    });
    themeTokens = styles.cardContent;
    mediaIds = ids.cardContent;
  } else {
    themeTokens = allTokens.current;
  }
  return /*#__PURE__*/_jsx(CardContentContainer, {
    ...themeTokens,
    flexContent: flexContent,
    withFooter: withFooter,
    ref: ref,
    "data-media": mediaIds,
    ...selectProps(rest),
    children: children
  });
});
CardContent.displayName = 'CardContent';
CardContent.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Card section content.
   */
  children: PropTypes.node,
  /**
   * Card tokens.
   */
  tokens: PropTypes.shape({
    ...getTokensPropType('Card'),
    alignSelf: PropTypes.string
  }),
  /**
   * Card variant.
   */
  variant: variantProp.propType,
  /**
   * Whether the card has a footer.
   */
  withFooter: PropTypes.bool
};
export default CardContent;